import { useState, useMemo } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Sparkles,
  Pencil,
  Trash2,
  BookOpen,
  Users,
  Video,
  Search,
  LayoutGrid,
  List,
  ExternalLink,
  Clock,
  Layers,
  X,
  Globe,
} from 'lucide-react';
import type { ProgramSubject, LessonSession } from '@lms/shared/types';
import { Button } from '@lms/ui';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';

interface SubjectCurriculumViewProps {
  subject: ProgramSubject;
  stageId: string;
  stageName?: string;
  onAddSession: (subjectId: string, subjectName: string) => void;
  onBulkGenerate: (subjectId: string, subjectName: string) => void;
  onEditSubject: (subject: ProgramSubject) => void;
  onDeleteSubject: (subjectId: string) => void;
  onEditSession: (session: LessonSession, subjectId: string, subjectName: string) => void;
  onDeleteSession: (sessionId: string) => void;
  isDeletingSubject?: boolean;
  isDeletingSession?: boolean;
  defaultExpanded?: boolean;
}

export function SubjectCurriculumView({
  subject,
  onAddSession,
  onBulkGenerate,
  onEditSubject,
  onDeleteSubject,
  onEditSession,
  onDeleteSession,
  isDeletingSubject = false,
  isDeletingSession = false,
  defaultExpanded = true,
}: SubjectCurriculumViewProps) {
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [selectedBatch, setSelectedBatch] = useState<number | 'all'>('all');

  const sessions = useMemo(() => subject.sessions || [], [subject.sessions]);
  const totalSessions = sessions.length;

  // Batch chunk size for pagination / quick tab switching when sessions are many (e.g. 40+)
  const BATCH_SIZE = 10;
  const totalBatches = Math.ceil(totalSessions / BATCH_SIZE);

  // Filtered sessions based on search and batch
  const filteredSessions = useMemo(() => {
    let list = [...sessions];

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        s =>
          s.title.toLowerCase().includes(q) ||
          String(s.sessionNumber).includes(q) ||
          (s.description && s.description.toLowerCase().includes(q))
      );
    } else if (selectedBatch !== 'all') {
      const startIdx = (selectedBatch - 1) * BATCH_SIZE;
      list = list.slice(startIdx, startIdx + BATCH_SIZE);
    }

    return list;
  }, [sessions, searchQuery, selectedBatch]);

  return (
    <div className="bg-surface-container-lowest rounded-3xl p-5 md:p-6 border border-outline-variant/10 shadow-sm transition-all">
      {/* Subject Header Card */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        {/* Left Info & Collapse Trigger */}
        <div className="flex items-start gap-3.5 flex-1 min-w-[260px]">
          <button
            type="button"
            onClick={() => setIsExpanded(prev => !prev)}
            className="p-1.5 mt-0.5 rounded-xl hover:bg-surface-container-high text-on-surface-variant/70 hover:text-primary transition-colors shrink-0"
            title={isExpanded ? 'Ciutkan Materi' : 'Buka Daftar Materi'}
          >
            {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </button>

          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h5
                onClick={() => setIsExpanded(prev => !prev)}
                className="text-base font-headline font-bold text-on-surface hover:text-primary transition-colors cursor-pointer"
              >
                {subject.name}
              </h5>

              {subject.code && (
                <span className="px-2 py-0.5 bg-surface-container-high text-on-surface-variant font-mono text-[9px] font-bold rounded-md">
                  {subject.code}
                </span>
              )}

              <span
                className={`px-2.5 py-0.5 text-[8px] font-black uppercase tracking-wider rounded-full ${
                  subject.isSequential
                    ? 'bg-primary/10 text-primary border border-primary/20'
                    : 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/30'
                }`}
              >
                {subject.isSequential
                  ? t(copy.learningPathStudio.badgeSequential)
                  : t(copy.learningPathStudio.badgeFlexible)}
              </span>

              <span className="px-2.5 py-0.5 text-[9px] font-bold rounded-full bg-primary/15 text-primary border border-primary/25">
                {totalSessions} Pertemuan
              </span>
            </div>

            {/* Badges metadata: Kitab, Pengajar, Prasyarat */}
            <div className="flex flex-wrap items-center gap-2 text-xs text-on-surface-variant">
              {subject.kitabRujukan && (
                <span className="flex items-center gap-1.5 bg-surface-container-low px-2.5 py-1 rounded-lg font-medium text-primary text-[11px]">
                  <BookOpen size={13} />
                  <span>{subject.kitabRujukan}</span>
                </span>
              )}

              {subject.teacherName && (
                <span className="flex items-center gap-1.5 bg-surface-container-low px-2.5 py-1 rounded-lg font-medium text-on-surface text-[11px]">
                  <Users size={13} />
                  <span>{subject.teacherName}</span>
                </span>
              )}

              {subject.prerequisiteSubject && (
                <span className="flex items-center gap-1 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 px-2.5 py-1 rounded-lg font-medium text-[10px] border border-amber-200/50 dark:border-amber-800/30">
                  <span>
                    {t(copy.learningPathStudio.badgePrerequisite)}{' '}
                    {subject.prerequisiteSubject.name}
                  </span>
                </span>
              )}
            </div>

            {subject.description && (
              <p className="text-xs text-on-surface-variant/70 pt-0.5 line-clamp-2">
                {subject.description}
              </p>
            )}
          </div>
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          <Button
            size="sm"
            onClick={() => onAddSession(subject.id, subject.name)}
            className="rounded-xl px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1 shadow-sm"
          >
            <Plus size={13} />
            <Bilingual {...copy.learningPathStudio.addSession} />
          </Button>

          <Button
            size="sm"
            variant="secondary"
            onClick={() => onBulkGenerate(subject.id, subject.name)}
            className="rounded-xl px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1"
          >
            <Sparkles size={13} />
            <Bilingual {...copy.learningPathStudio.bulkGenerateSessions} />
          </Button>

          <button
            type="button"
            onClick={() => onEditSubject(subject)}
            className="p-2 hover:bg-surface-container-high rounded-xl text-on-surface-variant/60 hover:text-primary transition-colors"
            title="Edit Mata Pelajaran"
          >
            <Pencil size={15} />
          </button>

          <button
            type="button"
            onClick={() => onDeleteSubject(subject.id)}
            disabled={isDeletingSubject}
            className="p-2 hover:bg-surface-container-high rounded-xl text-on-surface-variant/60 hover:text-error transition-colors disabled:opacity-50"
            title="Hapus Mata Pelajaran"
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      {/* Collapsible Session Content */}
      {isExpanded && (
        <div className="mt-6 pt-5 border-t border-outline-variant/10 space-y-4">
          {/* Toolbar for 40+ Sessions: Search, Batch Navigation, View Toggle */}
          {totalSessions > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 bg-surface-container-low/40 p-3 rounded-2xl">
              {/* Search input */}
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant/50"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    if (e.target.value.trim()) {
                      setSelectedBatch('all');
                    }
                  }}
                  placeholder="Cari pertemuan..."
                  className="w-full pl-8 pr-7 py-1.5 bg-surface-container-lowest border border-outline-variant/20 rounded-xl text-xs font-medium text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none focus:border-primary transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-surface-container-high rounded-full text-on-surface-variant/60"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>

              {/* Batch Chunk Tabs (e.g. 1-10, 11-20, 21-30, 31-40) */}
              {!searchQuery && totalBatches > 1 && (
                <div className="flex items-center gap-1 overflow-x-auto pb-0.5 scrollbar-hide">
                  <button
                    type="button"
                    onClick={() => setSelectedBatch('all')}
                    className={`px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all ${
                      selectedBatch === 'all'
                        ? 'bg-primary text-on-primary shadow-sm'
                        : 'bg-surface-container-lowest text-on-surface-variant hover:text-on-surface'
                    }`}
                  >
                    Semua ({totalSessions})
                  </button>
                  {Array.from({ length: totalBatches }).map((_, idx) => {
                    const batchNum = idx + 1;
                    const startSess = idx * BATCH_SIZE + 1;
                    const endSess = Math.min(totalSessions, (idx + 1) * BATCH_SIZE);
                    return (
                      <button
                        key={batchNum}
                        type="button"
                        onClick={() => setSelectedBatch(batchNum)}
                        className={`px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all whitespace-nowrap ${
                          selectedBatch === batchNum
                            ? 'bg-primary text-on-primary shadow-sm'
                            : 'bg-surface-container-lowest text-on-surface-variant hover:text-on-surface'
                        }`}
                      >
                        {startSess} - {endSess}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* View Mode Toggle (Table / Grid) */}
              <div className="flex items-center gap-1 bg-surface-container-lowest p-1 rounded-xl border border-outline-variant/10 shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`p-1.5 rounded-lg transition-colors ${
                    viewMode === 'table'
                      ? 'bg-primary/10 text-primary font-bold'
                      : 'text-on-surface-variant/60 hover:text-on-surface'
                  }`}
                  title="Tampilan Tabel Kompak"
                >
                  <List size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded-lg transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-primary/10 text-primary font-bold'
                      : 'text-on-surface-variant/60 hover:text-on-surface'
                  }`}
                  title="Tampilan Kartu Grid"
                >
                  <LayoutGrid size={14} />
                </button>
              </div>
            </div>
          )}

          {/* Session List Rendering */}
          {totalSessions === 0 ? (
            <div className="p-8 bg-surface-container-low/30 rounded-2xl text-center space-y-3 border border-dashed border-outline-variant/20">
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mx-auto">
                <Layers size={20} />
              </div>
              <div>
                <h6 className="font-headline font-bold text-on-surface text-xs">
                  <Bilingual {...copy.learningPathStudio.noSessions} />
                </h6>
                <p className="text-[11px] text-on-surface-variant/70 mt-0.5">
                  Tambahkan pertemuan satu per satu atau gunakan fitur generate untuk membuat hingga
                  40+ pertemuan otomatis.
                </p>
              </div>
              <div className="flex justify-center gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={() => onAddSession(subject.id, subject.name)}
                  className="rounded-xl px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider shadow-sm"
                >
                  <Plus size={13} className="mr-1" />
                  <Bilingual {...copy.learningPathStudio.addSession} />
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onBulkGenerate(subject.id, subject.name)}
                  className="rounded-xl px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider"
                >
                  <Sparkles size={13} className="mr-1" />
                  <Bilingual {...copy.learningPathStudio.bulkGenerateSessions} />
                </Button>
              </div>
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="p-6 bg-surface-container-low/20 rounded-2xl text-center text-xs text-on-surface-variant/70">
              Tidak ditemukan pertemuan dengan kata kunci &quot;{searchQuery}&quot;.
              <button
                onClick={() => setSearchQuery('')}
                className="block mx-auto mt-2 text-primary font-bold hover:underline"
              >
                Reset Pencarian
              </button>
            </div>
          ) : viewMode === 'table' ? (
            /* Compact High-Density Table / List View (Perfect for 40+ sessions) */
            <div className="overflow-hidden rounded-2xl border border-outline-variant/10 bg-surface-container-lowest shadow-sm">
              <div className="divide-y divide-outline-variant/10">
                {filteredSessions.map(session => {
                  const sessionMaterials = session.materials || [];
                  const meetingUrl = session.meetingUrl || sessionMaterials.find(material => material.meetingUrl)?.meetingUrl;

                  return (
                    <div
                      key={session.id}
                      className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-3 px-4 py-3 hover:bg-surface-container-low/50 transition-colors group/row"
                    >
                      {/* Left: Number + Title */}
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="w-8 h-8 rounded-xl bg-surface-container-high text-on-surface font-mono font-bold text-xs flex items-center justify-center shrink-0 shadow-sm">
                          #{session.sessionNumber}
                        </span>

                        <div className="min-w-0 flex-1">
                          <span className="text-xs font-bold text-on-surface block truncate">
                            {session.title}
                          </span>
                          {session.description && (
                            <p className="text-[10px] text-on-surface-variant/60 truncate mt-0.5">
                              {session.description}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Middle: Duration & Media Badges */}
                      <div className="flex items-center gap-2.5 shrink-0 flex-wrap">
                        {session.duration ? (
                          <span className="flex items-center gap-1 text-[11px] text-on-surface-variant/70 font-medium">
                            <Clock size={12} className="text-on-surface-variant/50" />
                            <span>{session.duration} mnt</span>
                          </span>
                        ) : null}

                        {meetingUrl ? (
                          <a
                            href={meetingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20 text-[10px] font-bold transition-colors"
                            title="Buka Tautan Tatap Muka (Meet/Zoom)"
                          >
                            <Globe size={11} />
                            <span>Tatap Muka</span>
                            <ExternalLink size={10} className="opacity-70" />
                          </a>
                        ) : null}

                        <span className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary/10 text-primary text-[10px] font-bold">
                          <Layers size={11} />
                          <span>{sessionMaterials.length} {t(copy.common.material)}</span>
                        </span>
                        {sessionMaterials.some(material => material.type === 'VIDEO') && (
                          <span className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-secondary/15 text-secondary text-[10px] font-bold">
                            <Video size={11} />
                            <span>Video</span>
                          </span>
                        )}
                      </div>

                      {/* Right: Actions */}
                      <div className="flex items-center gap-1 shrink-0 opacity-80 sm:opacity-0 group-hover/row:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => onEditSession(session, subject.id, subject.name)}
                          className="p-1.5 hover:bg-surface-container-high rounded-lg text-on-surface-variant/70 hover:text-primary transition-colors"
                          title="Edit Pertemuan"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDeleteSession(session.id)}
                          disabled={isDeletingSession}
                          className="p-1.5 hover:bg-surface-container-high rounded-lg text-on-surface-variant/70 hover:text-error transition-colors disabled:opacity-50"
                          title="Hapus Pertemuan"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Grid Card View */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredSessions.map(session => {
                const sessionMaterials = session.materials || [];
                const meetingUrl =
                  session.meetingUrl || sessionMaterials.find(material => material.meetingUrl)?.meetingUrl;

                return (
                  <div
                    key={session.id}
                    className="p-3.5 bg-surface-container-low/50 hover:bg-surface-container-low rounded-2xl border border-outline-variant/10 transition-all group/card flex flex-col justify-between gap-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="w-7 h-7 rounded-xl bg-surface-container-high text-on-surface font-mono font-bold text-xs flex items-center justify-center shrink-0 shadow-sm">
                          #{session.sessionNumber}
                        </span>
                        <div className="min-w-0">
                          <span className="text-xs font-bold text-on-surface block truncate">
                            {session.title}
                          </span>
                          {session.duration && (
                            <span className="text-[10px] text-on-surface-variant/60 font-medium">
                              {session.duration} mnt
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity shrink-0">
                        <button
                          type="button"
                          onClick={() => onEditSession(session, subject.id, subject.name)}
                          className="p-1 hover:bg-surface-container-high rounded text-on-surface-variant/60 hover:text-primary transition-colors"
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDeleteSession(session.id)}
                          disabled={isDeletingSession}
                          className="p-1 hover:bg-surface-container-high rounded text-on-surface-variant/60 hover:text-error transition-colors"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-1 border-t border-outline-variant/10 text-[10px] flex-wrap">
                      {meetingUrl && (
                        <a
                          href={meetingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-bold hover:underline"
                        >
                          <Globe size={11} /> Tatap Muka
                        </a>
                      )}

                      <span className="flex items-center gap-1 text-primary font-bold">
                        <Layers size={11} /> {sessionMaterials.length} {t(copy.common.material)}
                      </span>
                      {sessionMaterials.some(material => material.type === 'VIDEO') && (
                        <span className="flex items-center gap-1 text-secondary font-bold">
                          <Video size={11} /> Video
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
